import {
  getDoc,
  getDocs,
  query,
  setDoc,
  Unsubscribe,
  where,
} from "firebase/firestore";
import UserModel from "../models/UserModel";
import {
  callFunction,
  fromDocumentData,
  getCollectionRef,
  getDocumentRef,
  onDocumentSnapshot,
  toDocumentData,
  updateDocument,
} from "./FirebaseRepository";

class UserRepository {
  private path = "users";

  addUserOfAuthentication(uid: string, newData: UserModel): Promise<void> {
    const doc = getDocumentRef(this.path, uid);
    return setDoc(doc, newData);
  }

  getAllUsersFromDataBaseByUid(uids: string[]): Promise<UserModel[]> {
    return Promise.all(
      uids.map((uid) => {
        const docRef = getDocumentRef(this.path, uid);
        return getDoc(docRef);
      })
    ).then((docs) => this.convertDocsToModel(docs));
  }

  async getUserFromDataBaseByUid(uid: string): Promise<UserModel> {
    const doc = getDocumentRef(this.path, uid, converter);
    const snapshot = await getDoc(doc);
    if (snapshot.exists()) return snapshot.data() as UserModel;

    throw new Error("Document not found!");
  }

  getUsersFromDataBaseByEmail(emails: string[]): Promise<UserModel[]> {
    const collection = getCollectionRef(this.path, converter);
    const queryRef = query(collection, where("email", "in", emails));
    return getDocs(queryRef).then((snapshot) => {
      const users: UserModel[] = [];
      snapshot.docs.forEach((doc) => {
        if (doc.exists()) users.push(doc.data() as UserModel);
      });
      return users;
    });
  }

  public async getUsersFromDataBaseByRoleId(
    roleUid: string
  ): Promise<UserModel[]> {
    const collection = getCollectionRef(this.path, converter);
    const queryRef = query(collection, where("roleId", "==", roleUid));
    const snapshot = await getDocs(queryRef);
    return snapshot.docs.map((doc) => converter.fromFirestore(doc, undefined));
  }

  updateUserOnDataBase(
    uid: string,
    updatedData: Partial<UserModel>
  ): Promise<void> {
    delete updatedData["name"];
    const doc = getDocumentRef(this.path, uid);
    const data = converter.toFirestore(updatedData);
    return updateDocument(doc, data);
  }

  updateUserProfile(userId: string, name: string, photoUrl: string) {
    return callFunction("updateProfileInfos", {
      uid: userId,
      displayName: name,
      photoURL: photoUrl,
    });
  }

  subscribeUserSnapshot(
    uid: string,
    onChange: (data: UserModel) => void
  ): Unsubscribe {
    const doc = getDocumentRef(this.path, uid);
    return onDocumentSnapshot<UserModel>(doc, onChange, onChange, (error) =>
      console.error("UserSnapshot", error)
    );
  }

  async updateStatus(item: any): Promise<any> {
    let response = await callFunction("disableOrActiveUser", {
      id: item.id,
      uid: item.data.uid,
      email: item.data.email,
      isDisabled: item.data.disabled,
    });

    return response;
  }

  async updateAccess(item: any): Promise<void> {
    await callFunction("changeUserAccess", {
      companyUid: item.companyUid,
      uid: item.data.uid,
      access: item.data.access,
      email: item.data.email,
    });
  }

  async deleteUser(item: any): Promise<any> {
    let response = callFunction("removeUser", {
      id: item.id,
      uid: item.data.uid,
      email: item.data.email,
      companyUid: item.data.companyUid,
    });

    return response;
  }

  async createAcount(state: any): Promise<any> {
    let response = await callFunction("addUser", {
      email: state.email,
      emailVerified: false,
      password: state.password,
      displayName: state.displayName,
      disabled: false,
      companyUid: state.companyUid,
      currentUser: state.currentUser,
      faturamento: true,
      orcamento: true,
    });

    return response;
  }

  private convertDocsToModel(docs: any[]): UserModel[] {
    return docs.reduce((result, doc) => {
      if (doc.exists()) {
        const data = converter.fromFirestore(doc, undefined);
        if (data) {
          result.push(data);
        }
      }

      return result;
    }, new Array<UserModel>());
  }
}

// Firestore data converter
const converter = {
  toFirestore: (user: Partial<UserModel>) => {
    const data = user instanceof UserModel ? user.toSave() : user;
    return toDocumentData(data);
  },
  fromFirestore: (snapshot, options) => {
    let data = snapshot.data(options);
    data = fromDocumentData(data);

    return new UserModel(
      snapshot.id,
      data.displayName,
      data.email,
      data.creationTime,
      data.lastSignInTime,
      data.token,
      data.emailVerified,
      data.isAnonymous,
      data.providerId,
      data.rubk,
      data.photoURL,
      data.phoneNumber,
      data.products,
      data.lastSignOnFaturamento,
      data.disabled,
      data.access,
      data.roleId,
      data.contractStatusFiltered,
      data.dashboardFilters
    );
  },
};

export default new UserRepository();
